import { Dot, Popover, Tooltip } from "@xola/ui-kit";
import { RowNode } from "ag-grid-community";
import { isAddonField } from "common/src/models/Field";
import { keys, sum } from "lodash";
import React from "react";
import { AddonFieldResponse, FieldType } from "types";
import { getAddonLineItems } from "../../../../models/PurchaseItem";
import { RowData } from "./types";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import { AddonLineItem } from "@/stores/createPurchaseStore";

export const addOnsCellValueGetter = ({ data }: { data: RowData }) => {
    if ("addOns" in data) {
        return Object.values(data.addOns?.value ?? {}).reduce(
            (previousValue, currentValue) => previousValue + currentValue,
            0,
        );
    }

    return (
        "item" in data &&
        getAddonLineItems(data.item).reduce((previousValue, addon) => {
            return (addon.quantity ?? 0) + previousValue;
        }, 0)
    );
};

export const AddOnsCellRenderer = React.memo(({ data, node }: { data: RowData; node: RowNode }) => {
    const addOns =
        "addOns" in data ? keys(data.addOns?.value ?? {}).filter((name) => (data.addOns?.value[name] ?? 0) > 0) : [];

    // Header Expanded Cell
    if (!node.expanded && node.key === "summary" && "addOns" in data) {
        const demographicsCountValues = Object.values(data.addOns?.value ?? {});
        const totalCount = sum(demographicsCountValues);

        return (
            <div>
                <Popover appendTo={document.body} disabled={totalCount === 0}>
                    <span className="underline decoration-dotted">
                        <var data-var="totalcount">{totalCount}</var> Add-ons
                    </span>
                    <Popover.Content>
                        <div className="min-w-[160px] p-3">
                            <ul className="no-translate leading-5">
                                {addOns.map((name: string) => (
                                    <li key={name} className="flex items-center justify-between space-x-4">
                                        <span className="flex items-center space-x-2">
                                            <span>{name}</span>
                                        </span>
                                        <span>{data.addOns?.value[name]}</span>
                                    </li>
                                ))}
                            </ul>
                            <hr className="mt-2 mb-2 text-gray-light" />
                            <span className="flex justify-between font-bold">
                                <span>Total</span>
                                <span>
                                    <var data-var="totalcount">{totalCount}</var>
                                </span>
                            </span>
                        </div>
                    </Popover.Content>
                </Popover>
            </div>
        );
    }

    // Header Non-Expanded Cell
    if ("addOns" in data && data.addOns?.value) {
        return (
            <ul className="no-translate pt-2 leading-5">
                {addOns.map((name: string) => (
                    <li key={name} className="flex items-center space-x-1" data-testid="roster-guest-list-addons-item">
                        <div className="w-[calc(100%-5px)]">
                            <AutoTooltipContainer tooltipContent={`${data.addOns?.value[name]} ${name}`}>
                                <Dot className="mr-1 flex-shrink-0 bg-black" />
                                <span>
                                    {data.addOns?.value[name]} {name}
                                </span>
                            </AutoTooltipContainer>
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    // Individual Guest row cell - Show guest-specific add-ons
    if ("guest" in data && data.guest?.formResponse?.fields) {
        const lineItemTemplateNameMap: Record<string, string> = {};

        if (data.item) {
            for (const lineItem of getAddonLineItems(data.item)) {
                if (lineItem.template?.id && lineItem.name) {
                    lineItemTemplateNameMap[lineItem.template.id] = lineItem.name;
                }
            }
        }
        const guestAddOns = data.guest.formResponse.fields
            .filter((field) => field.type === FieldType.ADDON && field.value && Number(field.value) > 0)
            .map((field) => {
                const templateId = isAddonField(field.type)
                    ? (field as AddonFieldResponse).lineItemTemplate?.id
                    : undefined;

                return {
                    name: field.label,
                    quantity: Number(field.value),
                    lineItemTemplateId: templateId,
                };
            });

        if (guestAddOns.length > 0) {
            return (
                <ul className="no-translate pt-2 leading-5">
                    {guestAddOns.map((addon) => {
                        const addonName =
                            addon.lineItemTemplateId && lineItemTemplateNameMap[addon.lineItemTemplateId]
                                ? lineItemTemplateNameMap[addon.lineItemTemplateId]
                                : addon.name;
                        return (
                            <li
                                key={`${addon.name}-${addon.quantity}`}
                                className="flex items-center space-x-1"
                                data-testid="roster-guest-list-guest-addons-item"
                            >
                                <div className="w-[calc(100%-5px)]">
                                    <AutoTooltipContainer tooltipContent={`${addon.quantity} ${addon.name}`}>
                                        <Dot className="mr-1 flex-shrink-0 bg-black" />
                                        <span>
                                            {addon.quantity} {addonName}
                                        </span>
                                    </AutoTooltipContainer>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            );
        }
    }

    // Organizer row cell
    if ("isOrganizer" in data) {
        const addonItems = getAddonLineItems(data.item).filter((addon: AddonLineItem) => (addon.quantity ?? 0) > 0);

        return (
            <ul className="no-translate pt-2 leading-5">
                {addonItems.map((addon: AddonLineItem) => (
                    <li
                        key={addon.id}
                        className="flex items-center space-x-1"
                        data-testid="roster-guest-list-organizer-addons-item"
                    >
                        <Tooltip
                            offset={[0, 10]}
                            arrow={false}
                            content={<MultipleAddOnNames addonItems={addonItems} />}
                        >
                            <div className="w-[120px]">
                                <span className="flex items-center">
                                    <span>{addon.quantity}</span>&nbsp;
                                    <span className="block w-[120px] truncate" title={addon.name}>
                                        {addon.name}
                                    </span>
                                </span>
                            </div>
                        </Tooltip>
                    </li>
                ))}
            </ul>
        );
    }

    return null;
});

const MultipleAddOnNames = ({ addonItems }: { addonItems: AddonLineItem[] }) => {
    return (
        <div className="flex flex-col space-y-1">
            {addonItems.map((item) => (
                <div key={item.id} className="flex items-center">
                    <span>{item.quantity}</span>&nbsp;
                    <span>{item.name}</span>
                </div>
            ))}
        </div>
    );
};
